import React from 'react';
import PublicHeader from './PublicHeader';
import {
  GROPER_ARTICLE,
  GROPER_IMAGE_SRC,
  GROPER_IMAGE_960,
  GROPER_IMAGE_1600,
  GROPER_IMAGE_WIDTH,
  GROPER_IMAGE_HEIGHT,
} from '../content/groperStory';

interface Props {
  onNavigateHome: () => void;
  onNavigateToAnnouncements: () => void;
}

/**
 * Static vintage-newspaper-style article for "Tumbulgum's Groper", reachable
 * via /club-history/tumbulgums-groper from the landing page's pinned card.
 * Body text is verbatim from the clubhouse sign (see content/groperStory.ts).
 * The figure shows the full image: w-full h-auto, no object-fit, no cropping.
 */
const GroperArticlePage: React.FC<Props> = ({ onNavigateHome, onNavigateToAnnouncements }) => (
  <main className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
    <PublicHeader title="Club History" />

    <div className="max-w-[860px] mx-auto pt-6 px-4 flex items-center justify-between gap-4">
      <button
        type="button"
        onClick={onNavigateToAnnouncements}
        className="text-sm text-fishing-blue hover:underline"
      >
        ← All Announcements
      </button>
      <button
        type="button"
        onClick={onNavigateHome}
        className="text-sm text-fishing-blue hover:underline"
      >
        Back to Home
      </button>
    </div>

    <div className="py-8 sm:py-12 px-4">
      <article
        aria-labelledby="groper-article-heading"
        className="max-w-[860px] mx-auto bg-[#f4efe2] text-[#1a1a1a] font-serif border border-[#e2d9c3] shadow-xl px-6 py-7 sm:px-12 sm:py-10"
      >
        <header>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="font-extrabold text-2xl sm:text-3xl uppercase tracking-wide">
              {GROPER_ARTICLE.masthead}
            </p>
            <p className="italic text-xs text-[#5a5244]">{GROPER_ARTICLE.mastheadPlace}</p>
          </div>
          <div className="border-t-[3px] border-double border-[#2b2b2b] mt-3 mb-2" />
          <p className="text-center uppercase text-xs tracking-[0.18em] text-[#5a5244] mb-5">
            {GROPER_ARTICLE.dateline}
          </p>
          <h1
            id="groper-article-heading"
            className="text-3xl sm:text-[2.7rem] font-extrabold leading-tight text-center mb-3"
          >
            {GROPER_ARTICLE.headline}
          </h1>
          <p className="text-center uppercase font-bold text-sm sm:text-base tracking-wide mb-6">
            {GROPER_ARTICLE.subhead}
          </p>
        </header>

        <figure className="mb-7">
          <img
            src={GROPER_IMAGE_1600}
            srcSet={`${GROPER_IMAGE_960} 960w, ${GROPER_IMAGE_1600} 1600w, ${GROPER_IMAGE_SRC} 2600w`}
            sizes="(min-width: 960px) 900px, 100vw"
            width={GROPER_IMAGE_WIDTH}
            height={GROPER_IMAGE_HEIGHT}
            alt={GROPER_ARTICLE.figureAlt}
            decoding="async"
            className="block w-full h-auto border-[6px] border-white shadow-md"
          />
          <figcaption className="italic text-center text-sm text-[#5a5244] mt-3">
            {GROPER_ARTICLE.figcaption}
          </figcaption>
        </figure>

        <div className="columns-1 md:columns-2 gap-[30px] text-justify leading-relaxed">
          {GROPER_ARTICLE.paragraphs.map((paragraph, index) => (
            <p
              key={index}
              className={
                index === 0
                  ? 'mb-3 first-letter:float-left first-letter:text-[3.1rem] first-letter:leading-[0.82] first-letter:font-extrabold first-letter:pr-2 first-letter:pt-1'
                  : 'mb-3'
              }
            >
              {paragraph}
            </p>
          ))}
        </div>

        <div className="border-t border-[#b8ad94] mt-6 pt-4">
          <p className="italic text-center text-sm text-[#5a5244]">{GROPER_ARTICLE.footer}</p>
        </div>
      </article>
    </div>
  </main>
);

export default GroperArticlePage;
